/**
 * What to tell a user when the engine refuses a request.
 *
 * The engine answers `{ "error": "<message>" }`, and its messages name the rule that was broken
 * — which asset lacks a `proj:code`, which token name is taken, which object it cannot see.
 * That is far more use than anything we could compose from a status code, so it is shown as it
 * was said. We only supply words when the engine supplied none.
 *
 * Beside the engine slice rather than in any one view: every screen that writes through
 * `engineApiSlice` has the same two cases to tell apart, and a request that never landed has no
 * body to read at all — which is the case a plain `data?.error ?? fallback` silently gets wrong.
 */
export type EngineError = {
    status?: number | string;
    data?: unknown;
    /** RTK Query's transport failures carry their reason here rather than in `data`. */
    error?: string;
};

const statedReason = (data: unknown): string | undefined => {
    if (typeof data === 'object' && data !== null && 'error' in data) {
        const { error } = data as { error?: unknown };
        if (typeof error === 'string' && error.trim().length > 0) {
            return error;
        }
    }
    return undefined;
};

const UNEXPLAINED = 'The engine returned an error without saying why.';

/**
 * @param whenUnexplained what to say when the engine refuses without giving a reason. Callers
 * with wording of their own for a particular screen pass it here rather than testing for the
 * default and replacing it.
 */
export const engineErrorMessage = (error: EngineError | undefined, whenUnexplained: string = UNEXPLAINED): string | undefined => {
    if (!error) {
        return undefined;
    }

    // A request that never landed has no body to read, so the status is the whole story.
    if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR') {
        return 'The engine could not be reached. Check your connection and try again.';
    }

    return statedReason(error.data) ?? whenUnexplained;
};

/**
 * One rule a document did not meet, and where.
 *
 * `pointer` is RFC 6901 as the engine sent it — `/license`, `/extent/spatial/bbox`.
 * `path` is that pointer already parsed into its individual segments.
 */
export type SchemaViolation = {
    pointer: string;
    message: string;
    path: string[];
};

type ReportedViolation = Pick<SchemaViolation, 'pointer' | 'message'>;

/**
 * The segments of an RFC 6901 pointer.
 *
 * The two characters that would otherwise be ambiguous inside a name are escaped: `~1` is a
 * slash, `~0` a tilde. They are unescaped in that order — the other way round, `~01` would
 * become `~1` and then a slash, which is a different name entirely.
 *
 * A pointer begins with the separator, so the split leaves an empty first segment, which is
 * dropped. An empty pointer is the document itself and has no segments at all.
 */
const pathOfPointer = (pointer: string): string[] =>
    pointer
        .split('/')
        .slice(1)
        .map(segment => segment.replace(/~1/g, '/').replace(/~0/g, '~'));

const isViolation = (candidate: unknown): candidate is ReportedViolation =>
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof (candidate as ReportedViolation).pointer === 'string' &&
    typeof (candidate as ReportedViolation).message === 'string';

/**
 * Which rules the engine says a document broke.
 *
 * Empty for every refusal that is not about a field — a conflict, a request that never landed —
 * because the engine omits the list rather than sending an empty one. An entry that does not
 * name both a place and a reason is dropped: the body is the engine's, but a half-formed entry
 * would render as `undefined` beside an input.
 */
export const asEngineViolations = (error: EngineError | undefined): SchemaViolation[] => {
    const data = error?.data;
    if (typeof data !== 'object' || data === null || !('violations' in data)) {
        return [];
    }

    const { violations } = data as { violations?: unknown };
    if (!Array.isArray(violations)) {
        return [];
    }

    return violations.filter(isViolation).map(violation => ({ ...violation, path: pathOfPointer(violation.pointer) }));
};

/**
 * Why the value at this path was refused, if it was.
 *
 * The path is given as segments, so a caller never reproduces the pointer's separator and a
 * name that contains one cannot be mistaken for two. It must match in full: a refusal about
 * `/assets/tiles/href` is not a refusal about `/assets`, nor about another asset's `href`.
 *
 * The first rule is the one answered with — they arrive in the order the schema reported them.
 */
export const refusalAt = (violations: SchemaViolation[], ...path: string[]): string | undefined =>
    violations.find(violation => violation.path.length === path.length && violation.path.every((segment, at) => segment === path[at]))
        ?.message;
